import React, { useState } from "react";
import { MessageCircle, Mail, Clock, Send, Loader2, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useGet } from "@/lib/hooks";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

export default function Contact() {
  const { data: content } = useGet("/content");
  const { data: faqs } = useGet("/faqs");
  const wa = String(content?.whatsapp_number || "").replace(/\D/g, "");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return toast.error("Please share your name and a short message.");
    setLoading(true);
    try { await api.post("/contact", form); toast.success("Thank you! We'll get back to you soon 💝"); setForm({ name: "", email: "", phone: "", message: "" }); }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const waHref = `https://wa.me/${wa}?text=${encodeURIComponent("Hi Wonder Parcel! I have a question.")}`;

  return (
    <div className="wp-container py-10 sm:py-14">
      <SectionHeading eyebrow="We're here to help" title="Get in touch" subtitle="Questions about a gift, an order, or something custom? We'd love to hear from you." align="center" />

      <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1.2fr] lg:items-start">
        {/* Left: channels */}
        <div className="space-y-4">
          <a href={waHref} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-3 rounded-wp-lg border border-border bg-card p-5 shadow-wp-soft transition-transform hover:-translate-y-0.5">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-wp-success/10 text-wp-success"><MessageCircle className="h-5 w-5" /></span>
            <div><p className="font-display text-base">WhatsApp</p><p className="text-sm text-wp-ink-muted">Fastest way to reach us</p></div>
          </a>
          <a href="mailto:hello@wonderparcel.in" data-testid="contact-email" className="flex items-center gap-3 rounded-wp-lg border border-border bg-card p-5 shadow-wp-soft transition-transform hover:-translate-y-0.5">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-wp-blush-100 text-wp-rose-strong"><Mail className="h-5 w-5" /></span>
            <div><p className="font-display text-base">Email</p><p className="text-sm text-wp-ink-muted">hello@wonderparcel.in</p></div>
          </a>
          <div className="rounded-wp-lg border border-border bg-card p-5 shadow-wp-soft">
            <div className="flex items-center gap-3"><span className="grid h-11 w-11 place-items-center rounded-full bg-wp-mint/30 text-wp-ocean"><Clock className="h-5 w-5" /></span>
              <div><p className="font-display text-base">Business hours</p><p className="text-sm text-wp-ink-muted">Mon–Sat, 10am–7pm IST</p></div>
            </div>
            <p className="mt-3 rounded-wp-md bg-wp-surface px-3 py-2 text-xs text-wp-ink-muted">We typically respond within a few hours, and always within 24 hours.</p>
          </div>
        </div>

        {/* Right: form */}
        <form onSubmit={submit} className="space-y-4 rounded-wp-xl border border-border bg-card p-6 shadow-wp-card" data-testid="contact-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label className="mb-1.5 block text-sm">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-wp-md" data-testid="contact-name" /></div>
            <div><Label className="mb-1.5 block text-sm">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-wp-md" data-testid="contact-phone" /></div>
          </div>
          <div><Label className="mb-1.5 block text-sm">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-wp-md" data-testid="contact-email-input" /></div>
          <div><Label className="mb-1.5 block text-sm">Message</Label><Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-wp-md" data-testid="contact-message" /></div>
          <Button type="submit" disabled={loading} className="w-full rounded-xl bg-wp-rose-strong py-5 text-white hover:brightness-105" data-testid="contact-submit">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send message
          </Button>
        </form>
      </div>

      {/* FAQ */}
      {(faqs || []).length > 0 && (
        <div className="mx-auto mt-14 max-w-2xl" id="faq" data-testid="contact-faq">
          <h2 className="text-center font-display text-2xl sm:text-3xl">Frequently asked</h2>
          <Accordion type="single" collapsible className="mt-6">
            {(faqs || []).map((f) => (
              <AccordionItem key={f.id} value={f.id} className="border-border">
                <AccordionTrigger className="text-left text-sm font-medium">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-wp-ink-muted">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
}
